import React, { useId } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { consentPrimaryClass, consentSecondaryClass } from "./consentStyles";

/**
 * The first question a visitor is asked about cookies.
 *
 * Not modal: the page stays usable behind it, because a wall that has to be
 * answered before anything else works would make the answer less than free.
 * And no close button, since closing would have to mean something — and the
 * only honest meaning, "no", already has a button of its own.
 */
const CookieBanner = ({ onAcceptAll, onCustomize, onRejectOptional }) => {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className="cookie-banner fixed inset-x-0 bottom-0 z-90 p-3 sm:p-4"
    >
      <div className="mx-auto w-full max-w-5xl rounded-card border border-ink-200 bg-white p-4 shadow-card-hover sm:p-5">
        <h2 id={titleId} className="text-base font-semibold text-ink-900">
          {t("CookieConsent.greeting")}
        </h2>
        <p className="mt-1 text-fluid-sm leading-relaxed text-ink-600">
          {t("CookieConsent.message")}{" "}
          <Link
            to="/cookies"
            className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
          >
            {t("CookieConsent.policyLink")}
          </Link>
        </p>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
          <button
            type="button"
            className={`cookie-accept-all ${consentPrimaryClass}`}
            onClick={onAcceptAll}
          >
            {t("CookieConsent.acceptAll")}
          </button>
          <button
            type="button"
            className={`cookie-customize ${consentSecondaryClass}`}
            onClick={onCustomize}
          >
            {t("CookieConsent.customize")}
          </button>
          <button
            type="button"
            className={`cookie-reject-optional ${consentSecondaryClass}`}
            onClick={onRejectOptional}
          >
            {t("CookieConsent.rejectOptional")}
          </button>
        </div>
      </div>
    </section>
  );
};

CookieBanner.propTypes = {
  onAcceptAll: PropTypes.func.isRequired,
  onCustomize: PropTypes.func.isRequired,
  onRejectOptional: PropTypes.func.isRequired,
};

export default CookieBanner;
